// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: the "Move to server" shortcut (emoji-plan.md section 14): a row on the emoji info card (the desktop
// popout and the phone-width sheet) and an item in the reactions modal for the selected reaction. Shown only for a
// custom emoji whose server you're in, with the move rule (Manage Expressions there and in another server). Selecting
// it closes what it is in, then opens the move drawer preset to that emoji once that has fully closed (section 17).
// Exclude from upstream sync.

import {canMoveEmojiFrom, openEmojiMoveDrawer} from '@app/features/emoji/components/emojis/EmojiMoveDrawer';
import styles from '@app/features/emoji/components/emojis/EmojiMoveShortcut.module.css';
import {MOVE_TO_SERVER_DESCRIPTOR} from '@app/features/expressions/utils/EmojiMove';
import Guilds from '@app/features/guild/state/Guilds';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import {ArrowRightIcon} from '@app/features/ui/icons/DirectionalIcons';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';

const NOTHING_TO_UPDATE = () => {};
/** The router stops waiting for a history step after 500 ms; past that, the step is not coming. */
const HISTORY_STEP_CAP_MS = 600;
/** However long the closing takes, the drawer opens by then. */
const CLOSE_CAP_MS = 1500;
const CHECK_EVERY_MS = 16;

/** A bottom sheet or a modal owns the current history entry; closing it steps history back (a popstate). */
function closingStepsHistoryBack(): boolean {
	const state = RouterUtils.getHistory()?.getLocation().state;
	return state != null && typeof state === 'object' && ('bottomSheet' in state || 'modal' in state);
}

/**
 * Runs `open` once what `anchor` sits in has fully closed: `anchor` has left the page (the phone-width sheet unmounts
 * 300 ms after closing, then gives focus back), and when the closing sheet or modal owned the current history entry,
 * its step back has happened. The drawer's own back handler takes any later step back for a Back press and closes it,
 * which made it flash on a phone (emoji-plan.md section 17).
 */
export function openAfterClosing(anchor: Element, expectHistoryStep: boolean, open: () => void): void {
	const startedAt = Date.now();
	let stepped = !expectHistoryStep;
	const onPopState = () => {
		stepped = true;
	};
	if (!stepped) window.addEventListener('popstate', onPopState);
	const check = () => {
		const waited = Date.now() - startedAt;
		if (waited >= HISTORY_STEP_CAP_MS) stepped = true;
		if ((stepped && !anchor.isConnected) || waited >= CLOSE_CAP_MS) {
			window.removeEventListener('popstate', onPopState);
			open();
			return;
		}
		window.setTimeout(check, CHECK_EVERY_MS);
	};
	window.setTimeout(check, CHECK_EVERY_MS);
}

/** The emoji's server when the shortcut may show: a custom emoji of a server you're in, with the move rule; else null. */
export function emojiMoveShortcutGuild(
	emojiId: string | null | undefined,
	guildId: string | null | undefined,
): string | null {
	if (!emojiId || !guildId) return null;
	if (Guilds.getGuild(guildId) == null) return null;
	return canMoveEmojiFrom(guildId) ? guildId : null;
}

export const EmojiMoveShortcut = observer(function EmojiMoveShortcut({
	emojiId,
	guildId,
	onSelect,
	className,
}: {
	emojiId: string | null | undefined;
	guildId: string | null | undefined;
	/** Closes the card or modal the shortcut is in, before the drawer opens. */
	onSelect: () => void;
	className?: string;
}) {
	const {i18n} = useLingui();
	const source = emojiMoveShortcutGuild(emojiId, guildId);
	if (source == null || !emojiId) return null;
	return (
		<button
			type="button"
			className={clsx(styles.row, className)}
			onClick={(event) => {
				const anchor = event.currentTarget;
				const expectHistoryStep = closingStepsHistoryBack();
				onSelect();
				openAfterClosing(anchor, expectHistoryStep, () => openEmojiMoveDrawer(source, emojiId, NOTHING_TO_UPDATE));
			}}
			data-flx="emoji.emojis.emoji-move-shortcut.row"
		>
			<ArrowRightIcon className={styles.icon} weight="bold" aria-hidden={true} />
			<span className={styles.label} data-flx="emoji.emojis.emoji-move-shortcut.label">
				{i18n._(MOVE_TO_SERVER_DESCRIPTOR)}
			</span>
		</button>
	);
});
